import { defineStore } from 'pinia';
import { ref } from 'vue';

import { CustomerViews, VideosSeen } from '@core/classes/SharedEnums';
import SignatureSessionInfo, { CurrentSigningSessionInfo } from '@core/classes/Paperwork/SignatureSessionInfo';
import { meetingHub as $meetingHub } from '@core/plugins/meetingHub';
import FIMenuCustomerViewed from '@core/classes/FIMenuCustomerViewed';
import FIMenuForCustomerMeeting from '@core/classes/FIMenuForCustomerMeeting';
import { getDateInstance } from '@core/services/util.date';
import { SupportedLanguages } from '@core/_globals/types/sharedTypes';

export const useFIMenuForCustomerMeetingStore = defineStore('fimenuForCustomerMeeting', () => {
	// State
	const fimenu = ref<FIMenuForCustomerMeeting | null>(null);
	const currentSigningSessionInfo = ref<CurrentSigningSessionInfo[]>([]);
	const signatureImg = ref<string>('');
	const initialsImg = ref<string>('');
	const entitySignatureImg = ref<string>('');
	const entityInitialsImg = ref<string>('');

	function clearFIMenu() {
		fimenu.value = new FIMenuForCustomerMeeting();
	}

	function setFIMenu(newFIMenu: FIMenuForCustomerMeeting) {
		fimenu.value = new FIMenuForCustomerMeeting(newFIMenu);
	}

	async function saveCustomerViewed(viewedType: CustomerViews, videosSeen: VideosSeen | null = null) {
		const viewedDateTime = getDateInstance();
		fimenu.value.customerViewed.push(
			new FIMenuCustomerViewed({
				viewedDateTime: viewedDateTime,
				viewedType: viewedType,
				videosSeen: videosSeen,
			}),
		);
		$meetingHub.saveCustomerViewed(viewedType, viewedDateTime, videosSeen);
	}

	//#region Payment Sheet Actions
	function setLanguage(language: SupportedLanguages) {
		fimenu.value.language = language;
	}

	function savePreferredCoverage(term: number) {
		saveCustomerViewed(CustomerViews.PaymentSheetPreferredCoverageFinished);
		fimenu.value.selectPreferredCoverageTerm(term);
		$meetingHub.setPreferredCoverage(term);
	}

	function setLoanDaysToPayment(days: number) {
		saveCustomerViewed(CustomerViews.PaymentSheetPaymentStartDateFinished);
		fimenu.value.loanTerms.selectedDaysToFirstPayment = days;
		$meetingHub.setLoanDaysToPayment(days);
	}

	async function setDraftLoanTerms(term: number, apr: number) {
		saveCustomerViewed(CustomerViews.PaymentSheetCarousel);

		// For draft deals, term / rates stored in loanTerms.term1
		fimenu.value.loanTerms.term1.term = term;
		fimenu.value.loanTerms.term1.sellrate = apr;
		fimenu.value.loanTerms.term1.buyrate = apr;

		$meetingHub.setDraftLoanTerms(Number(term), Number(apr), Number(apr));
	}

	function savePaymentCarousel(coverageTerm: number, term: number) {
		saveCustomerViewed(CustomerViews.PaymentSheetCarousel);

		$meetingHub.saveCustomerFImenu(JSON.stringify({ coverageTerm: coverageTerm, term: term }));
	}
	//#endregion

	function setCustomerSignatureImages(signature: string = '', initials: string = '', isEntitySigning: boolean = false) {
		// If there’s no signature or initials, clear all images
		if (!signature && !initials) {
			signatureImg.value = '';
			initialsImg.value = '';
			entitySignatureImg.value = '';
			entityInitialsImg.value = '';
			return;
		}
		if (isEntitySigning) {
			entitySignatureImg.value = signature;
			entityInitialsImg.value = initials;
		} else {
			signatureImg.value = signature;
			initialsImg.value = initials;
		}
	}

	return {
		fimenu,
		signatureImg,
		initialsImg,
		entitySignatureImg,
		entityInitialsImg,
		clearFIMenu,
		setFIMenu,
		saveCustomerViewed,
		setLanguage,
		savePreferredCoverage,
		setLoanDaysToPayment,
		setDraftLoanTerms,
		savePaymentCarousel,
		setCustomerSignatureImages,
		currentSigningSessionInfo,
	};
});
